/**
 * Runtime Configuration Loader and Management
 *
 * Loads all configuration variables at runtime from a JSON file served alongside the SPA,
 * replacing build-time environment variable baking. Supports environment-specific config
 * files (dev, stage, prod) with automatic detection and fallback.
 *
 * Architecture:
 * - Loads /runtime-config.json (or environment-specific variant) on app startup
 * - Validates configuration against TypeScript interfaces
 * - Provides config via React context to all components
 * - Implements graceful error handling with fallback defaults
 * - Supports runtime config overrides for testing
 *
 * See: docs/v2/runtime-config.md
 */

import { z } from "zod";

/**
 * Runtime configuration interface
 *
 * All configuration variables that were previously baked at build time
 * are now loaded at runtime from runtime-config.json
 */
export interface RuntimeConfig {
  // API configuration
  apiBaseUrl: string;

  // Supabase configuration
  supabaseUrl: string;
  supabaseAnonKey: string;
  supabaseServiceRoleKey?: string; // Optional for frontend, used for admin operations

  // External service integrations
  elevenLabsAgentId: string;
  elevenLabsApiKey?: string; // Optional: may be handled server-side only

  // Feature flags
  featureFlags?: {
    useModalSourcing?: boolean;
    useModalMatching?: boolean;
    useWorkersApi?: boolean;
    useWorkersWebhooks?: boolean;
    useSupabaseAuth?: boolean;
  };

  // Allow additional string keys for extensibility
  [key: string]: string | undefined | Record<string, unknown>;
}

/**
 * Zod schema for runtime config validation
 *
 * Enforces required fields and validates URL formats
 */
const RuntimeConfigSchema = z.object({
  apiBaseUrl: z
    .string()
    .url("API base URL must be a valid URL")
    .min(1, "API base URL is required"),
  supabaseUrl: z
    .string()
    .url("Supabase URL must be a valid URL")
    .min(1, "Supabase URL is required"),
  supabaseAnonKey: z.string().min(1, "Supabase anonymous key is required"),
  supabaseServiceRoleKey: z.string().optional(),
  elevenLabsAgentId: z.string().min(1, "ElevenLabs agent ID is required"),
  elevenLabsApiKey: z.string().optional(),
  featureFlags: z
    .object({
      useModalSourcing: z.boolean().optional(),
      useModalMatching: z.boolean().optional(),
      useWorkersApi: z.boolean().optional(),
      useWorkersWebhooks: z.boolean().optional(),
      useSupabaseAuth: z.boolean().optional(),
    })
    .optional(),
});

/**
 * Error result returned when config loading fails
 */
export interface ConfigErrorResult {
  success: false;
  error: Error;
  config: null;
}

/**
 * Success result returned when config loads successfully
 */
export interface ConfigSuccessResult {
  success: true;
  error: null;
  config: RuntimeConfig;
}

/**
 * Result type for config loading operations
 */
export type ConfigResult = ConfigSuccessResult | ConfigErrorResult;

/**
 * Detects current environment by checking window.location.hostname or environment variables
 *
 * @returns Environment name: 'prod', 'stage', or 'dev'
 */
export function detectEnvironment(): "prod" | "stage" | "dev" {
  // For browser runtime, check hostname
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname.includes("herohire.ai") && !hostname.includes("staging")) {
      return "prod";
    }
    if (hostname.includes("staging")) {
      return "stage";
    }
    // Default to dev for localhost, 127.0.0.1, etc.
    return "dev";
  }

  // Fallback for non-browser runtimes (testing, SSR, etc.)
  return "dev";
}

/**
 * Constructs the path to the runtime config JSON file based on environment
 *
 * Attempts to load environment-specific config first, falls back to generic config.json
 * Examples:
 * - /runtime-config.prod.json (for production)
 * - /runtime-config.stage.json (for staging)
 * - /runtime-config.dev.json (for development)
 * - /runtime-config.json (fallback)
 *
 * @param environment - Environment name
 * @returns Array of config paths to try in order
 */
export function getConfigPaths(
  environment: "prod" | "stage" | "dev",
): string[] {
  return [`/runtime-config.${environment}.json`, "/runtime-config.json"];
}

/**
 * Loads runtime configuration from JSON file
 *
 * Tries to fetch config from environment-specific files first, falls back to generic config.
 * Validates the loaded config against the TypeScript schema.
 * Throws descriptive errors if config is missing, malformed, or invalid.
 *
 * @param configPath - Path to config file (defaults to auto-detected path)
 * @returns Promise resolving to loaded and validated configuration
 * @throws Error if config cannot be loaded or is invalid
 *
 * @example
 * const config = await loadRuntimeConfig();
 * console.log(config.apiBaseUrl); // https://api.staging.herohire.ai
 */
export async function loadRuntimeConfig(
  configPath?: string,
): Promise<RuntimeConfig> {
  const environment = detectEnvironment();
  const paths = configPath ? [configPath] : getConfigPaths(environment);

  let lastError: Error | null = null;

  for (const path of paths) {
    try {
      console.log(`[config] Attempting to load config from ${path}`);
      const response = await fetch(path, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        // Don't cache config during development
        cache: environment === "prod" ? "default" : "no-store",
      });

      if (!response.ok) {
        lastError = new Error(
          `Failed to fetch runtime config from ${path}: ${response.status} ${response.statusText}`,
        );
        console.warn(`[config] ${lastError.message}`);
        continue; // Try next path
      }

      const rawConfig = await response.json();
      console.log(`[config] Loaded config from ${path}`);

      // Validate configuration against schema
      const validation = RuntimeConfigSchema.safeParse(rawConfig);
      if (!validation.success) {
        const errors = validation.error.errors
          .map((err) => `${err.path.join(".")}: ${err.message}`)
          .join("; ");
        throw new Error(`Invalid runtime config: ${errors}`);
      }

      console.log("[config] Runtime config loaded and validated successfully");
      return validation.data as RuntimeConfig;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      lastError = new Error(`Error loading config from ${path}: ${errorMsg}`);
      console.warn(`[config] ${lastError.message}`);
    }
  }

  // All paths failed
  const errorMsg = lastError
    ? lastError.message
    : `Unable to load runtime config from any of: ${paths.join(", ")}`;
  const error = new Error(errorMsg);
  console.error(`[config] ${errorMsg}`);
  throw error;
}

/**
 * Safely attempts to load runtime config with fallback defaults
 *
 * Unlike loadRuntimeConfig(), this function does NOT throw.
 * Instead, it returns a result object indicating success or failure.
 * If config loading fails, it returns null config but a descriptive error.
 *
 * This is useful for non-critical operations or graceful degradation.
 *
 * @param configPath - Optional override for config path
 * @returns Promise resolving to ConfigResult (success or error)
 */
export async function loadRuntimeConfigSafe(
  configPath?: string,
): Promise<ConfigResult> {
  try {
    const config = await loadRuntimeConfig(configPath);
    return {
      success: true,
      error: null,
      config,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error)),
      config: null,
    };
  }
}

/**
 * Creates a fallback/minimal config for graceful degradation
 *
 * If config loading fails but the app needs to bootstrap,
 * this returns a minimal valid config that allows the app to start
 * but may disable certain features.
 *
 * @returns Minimal valid RuntimeConfig
 */
export function getMinimalFallbackConfig(): RuntimeConfig {
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "localhost";
  const protocol =
    typeof window !== "undefined" ? window.location.protocol : "http:";

  return {
    apiBaseUrl: `${protocol}//${hostname}:8787/api`,
    supabaseUrl: `${protocol}//${hostname}:54321`, // Supabase default local port
    supabaseAnonKey: "anon-key-placeholder",
    elevenLabsAgentId: "agent-id-placeholder",
    featureFlags: {
      useModalSourcing: false,
      useModalMatching: false,
      useWorkersApi: true,
      useWorkersWebhooks: true,
      useSupabaseAuth: true,
    },
  };
}
